import { BaseRestRepository } from '~/repositories/baseRestRepository';
import { validateId } from '~/repositories/utils';
import { envConfig } from '~/utils/env';
import { urlWithQuery } from '~/utils/url';
import type {
  BankFeedConnectionDetailDto,
  BankFeedConnectionDto,
  BankFeedConnectionListDto,
  BankFeedLinkTokenDto,
  BankFeedPublicTokenExchangeDto,
  BankFeedPublicTokenExchangeResultDto,
  BankFeedReauthenticationCompletionDto,
  BankFeedRefreshAttemptDto,
  CreateReconciliationPeriodDto,
  DeletePeriodParams,
  GetItemsParams,
  GetMatchTargetsParams,
  GetPeriodItemsParams,
  GetPeriodParams,
  GetSettleTargetsParams,
  GetSummaryParams,
  ReconciliationItemDetailDto,
  ReconciliationItemsDto,
  ReconciliationMatchTargetDtoPaginationDto,
  ReconciliationPeriodDetailDto,
  ReconciliationPeriodDispositionDto,
  ReconciliationPeriodDto,
  ReconciliationPeriodItemsDto,
  ReconciliationPeriodsDto,
  ReconciliationSettleTargetDto,
  ReconciliationSettleTargetDtoPaginationDto,
  ReconciliationSummaryDto,
  ResolveReconciliationItemAcceptDto,
  ResolveReconciliationItemIgnoreDto,
  ResolveReconciliationItemMatchDto,
  ResolveReconciliationItemPostDto,
  ResolveReconciliationItemSettleDto,
  TransactionIdentity,
  UpdatePeriodParams,
  UpdateReconciliationPeriodDto,
} from '~/swagger/accounting/Api';

const BANK_FEEDS_URL = `${envConfig.service.accounting}/company-portal/bank-feeds`;
const RECONCILIATIONS_URL = `${envConfig.service.accounting}/company-portal/reconciliations`;

export class ReconciliationsRepository extends BaseRestRepository {
  public async getConnections(): Promise<BankFeedConnectionListDto> {
    const response = await this.fetchWithAuth(`${BANK_FEEDS_URL}/connections`);
    return this.getJsonResponse(response);
  }

  public async getConnectLinkToken(): Promise<BankFeedLinkTokenDto> {
    const response = await this.fetchWithAuth(`${BANK_FEEDS_URL}/link-token`, { method: 'POST' });
    return this.getJsonResponse(response);
  }

  public async getConnectionDetail(connectionId: string): Promise<BankFeedConnectionDetailDto> {
    validateId(connectionId, 'Bank feed connection');
    const response = await this.fetchWithAuth(`${BANK_FEEDS_URL}/connections/${connectionId}`);
    return this.getJsonResponse(response);
  }

  public async getUpdateLinkToken(connectionId: string): Promise<BankFeedLinkTokenDto> {
    validateId(connectionId, 'Bank feed connection');
    const response = await this.fetchWithAuth(`${BANK_FEEDS_URL}/connections/${connectionId}/link-token`, { method: 'POST' });
    return this.getJsonResponse(response);
  }

  public async exchangePublicToken(publicToken: string): Promise<BankFeedPublicTokenExchangeResultDto> {
    const payload: BankFeedPublicTokenExchangeDto = { publicToken };
    const response = await this.fetchWithAuth(`${BANK_FEEDS_URL}/public-token/exchange`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async removeConnection(connectionId: string): Promise<BankFeedConnectionDto> {
    validateId(connectionId, 'Bank feed connection');
    const response = await this.fetchWithAuth(`${BANK_FEEDS_URL}/connections/${connectionId}`, { method: 'DELETE' });
    return this.getJsonResponse(response);
  }

  public async refreshConnection(connectionId: string): Promise<BankFeedRefreshAttemptDto> {
    validateId(connectionId, 'Bank feed connection');
    const response = await this.fetchWithAuth(`${BANK_FEEDS_URL}/connections/${connectionId}/refresh`, { method: 'POST' });
    return this.getJsonResponse(response);
  }

  public async completeReauthentication(connectionId: string): Promise<BankFeedReauthenticationCompletionDto> {
    validateId(connectionId, 'Bank feed connection');
    const response = await this.fetchWithAuth(`${BANK_FEEDS_URL}/connections/${connectionId}/reauthentication/complete`, {
      method: 'POST',
    });
    return this.getJsonResponse(response);
  }

  public async getSummary(params: GetSummaryParams = {}): Promise<ReconciliationSummaryDto> {
    const url = urlWithQuery(`${RECONCILIATIONS_URL}/summary`, params);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getItems(params: GetItemsParams): Promise<ReconciliationItemsDto> {
    const url = urlWithQuery(`${RECONCILIATIONS_URL}/items`, params);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getItem(reconciliationItemId: string): Promise<ReconciliationItemDetailDto> {
    validateId(reconciliationItemId, 'Reconciliation item');
    const response = await this.fetchWithAuth(`${RECONCILIATIONS_URL}/items/${reconciliationItemId}`);
    return this.getJsonResponse(response);
  }

  public async acknowledgeReview(reconciliationItemId: string): Promise<ReconciliationItemDetailDto> {
    validateId(reconciliationItemId, 'Reconciliation item');
    const response = await this.fetchWithAuth(`${RECONCILIATIONS_URL}/items/${reconciliationItemId}/review/acknowledge`, {
      method: 'POST',
    });
    return this.getJsonResponse(response);
  }

  public async rematchItem(reconciliationItemId: string): Promise<ReconciliationItemDetailDto> {
    validateId(reconciliationItemId, 'Reconciliation item');
    const response = await this.fetchWithAuth(`${RECONCILIATIONS_URL}/items/${reconciliationItemId}/rematch`, {
      method: 'POST',
    });
    return this.getJsonResponse(response);
  }

  public async getSettleTargets(reconciliationItemId: string, identity: TransactionIdentity): Promise<ReconciliationSettleTargetDto[]> {
    validateId(reconciliationItemId, 'Reconciliation item');
    const url = urlWithQuery(`${RECONCILIATIONS_URL}/items/${reconciliationItemId}/settle-targets`, { identity });
    const response = await this.fetchWithAuth(url);
    const pagination = await this.getJsonResponse(response);
    return pagination.items;
  }

  public async getMatchTargets(
    reconciliationItemId: string,
    params: Omit<GetMatchTargetsParams, 'reconciliationItemId'>
  ): Promise<ReconciliationMatchTargetDtoPaginationDto> {
    validateId(reconciliationItemId, 'Reconciliation item');
    const url = urlWithQuery(`${RECONCILIATIONS_URL}/items/${reconciliationItemId}/match-targets`, params);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getSettleTargetsPaginated(
    reconciliationItemId: string,
    params: Omit<GetSettleTargetsParams, 'reconciliationItemId'>
  ): Promise<ReconciliationSettleTargetDtoPaginationDto> {
    validateId(reconciliationItemId, 'Reconciliation item');
    const url = urlWithQuery(`${RECONCILIATIONS_URL}/items/${reconciliationItemId}/settle-targets`, params);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async resolveItem(
    reconciliationItemId: string,
    payload:
      | ResolveReconciliationItemAcceptDto
      | ResolveReconciliationItemMatchDto
      | ResolveReconciliationItemSettleDto
      | ResolveReconciliationItemPostDto
      | ResolveReconciliationItemIgnoreDto
  ): Promise<ReconciliationItemDetailDto> {
    validateId(reconciliationItemId, 'Reconciliation item');
    const { type, ...rest } = payload;
    const response = await this.fetchWithAuth(`${RECONCILIATIONS_URL}/items/${reconciliationItemId}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ type, ...rest }),
    });
    return this.getJsonResponse(response);
  }

  public async generateCandidates(reconciliationItemId: string): Promise<ReconciliationItemDetailDto> {
    validateId(reconciliationItemId, 'Reconciliation item');
    const response = await this.fetchWithAuth(`${RECONCILIATIONS_URL}/items/${reconciliationItemId}/candidates/generate`, {
      method: 'POST',
    });
    return this.getJsonResponse(response);
  }

  public async getPeriods(bankAccountId: string): Promise<ReconciliationPeriodsDto> {
    validateId(bankAccountId, 'Bank account');
    const url = urlWithQuery(`${RECONCILIATIONS_URL}/periods`, { bankAccountId });
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async createPeriod(payload: CreateReconciliationPeriodDto): Promise<ReconciliationPeriodDto> {
    const response = await this.fetchWithAuth(`${RECONCILIATIONS_URL}/periods`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async getPeriod(params: GetPeriodParams): Promise<ReconciliationPeriodDetailDto> {
    validateId(params.reconciliationPeriodId, 'Reconciliation period');
    const response = await this.fetchWithAuth(`${RECONCILIATIONS_URL}/periods/${params.reconciliationPeriodId}`);
    return this.getJsonResponse(response);
  }

  public async getPeriodItems(params: GetPeriodItemsParams): Promise<ReconciliationPeriodItemsDto> {
    validateId(params.reconciliationPeriodId, 'Reconciliation period');
    const url = urlWithQuery(`${RECONCILIATIONS_URL}/periods/${params.reconciliationPeriodId}/items`, params);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async updatePeriod(params: UpdatePeriodParams, payload: UpdateReconciliationPeriodDto): Promise<ReconciliationPeriodDto> {
    validateId(params.reconciliationPeriodId, 'Reconciliation period');
    const response = await this.fetchWithAuth(`${RECONCILIATIONS_URL}/periods/${params.reconciliationPeriodId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async deletePeriod(params: DeletePeriodParams): Promise<void> {
    validateId(params.reconciliationPeriodId, 'Reconciliation period');
    await this.fetchWithAuth(`${RECONCILIATIONS_URL}/periods/${params.reconciliationPeriodId}`, {
      method: 'DELETE',
    });
  }

  public async lockPeriod(reconciliationPeriodId: string): Promise<ReconciliationPeriodDto> {
    validateId(reconciliationPeriodId, 'Reconciliation period');
    const response = await this.fetchWithAuth(`${RECONCILIATIONS_URL}/periods/${reconciliationPeriodId}/lock`, { method: 'POST' });
    return this.getJsonResponse(response);
  }

  public async reopenPeriod(reconciliationPeriodId: string): Promise<ReconciliationPeriodDto> {
    validateId(reconciliationPeriodId, 'Reconciliation period');
    const response = await this.fetchWithAuth(`${RECONCILIATIONS_URL}/periods/${reconciliationPeriodId}/reopen`, { method: 'POST' });
    return this.getJsonResponse(response);
  }

  public async acknowledgeDisposition(reconciliationPeriodId: string, dispositionId: string): Promise<ReconciliationPeriodDispositionDto> {
    validateId(reconciliationPeriodId, 'Reconciliation period');
    validateId(dispositionId, 'Reconciliation period disposition');
    const response = await this.fetchWithAuth(
      `${RECONCILIATIONS_URL}/periods/${reconciliationPeriodId}/dispositions/${dispositionId}/acknowledge`,
      { method: 'POST' }
    );
    return this.getJsonResponse(response);
  }
}

export const reconciliationsRepository = new ReconciliationsRepository();
